// Armado por zonas: reglas de selección y reductor puro. Sin React, sin Supabase.
//
// Diseño: docs/superpowers/specs/2026-07-29-pizza-zoned-modifiers-design.md
//
// D5 es la regla que más fácil se implementa mal: `max_selections` aplica POR ZONA y
// `min_selections` POR LÍNEA. No son simétricos. Contar el mínimo por zona exigiría
// una salsa en cada cuarto, en zonas que el cliente ni tocó.

import {
  ZONES_BY_LAYOUT,
  aggregateZonedSelections,
  collapseFullCoverage,
  zoneBelongsToLayout,
  type Zone,
  type ZoneLayout,
  type ZonedSelection,
} from "@/lib/menu/zonedPricing"

export type ZonedGroupRule = {
  groupId: string
  required: boolean
  /** 0 si no aplica. */
  minSelections: number
  /** 1 = single-select. */
  maxSelections: number
  /** D8: false para masa, tamaño y cualquier grupo que la cocina no puede partir. */
  zonable: boolean
  /** El grupo que gobierna el tamaño: max 1 POR LÍNEA. */
  isSizeGroup: boolean
}

export type ZonedValidation =
  | { ok: true }
  | {
      ok: false
      reason:
        | "zone_not_in_layout"
        | "group_not_zonable"
        | "max_per_zone"
        | "max_per_line"
        | "min_per_line"
      zone?: Zone
    }

/** Unidades que cuentan para topes y mínimos. Los removals NO cuentan: quitar un
 *  ingrediente no es seleccionar uno. */
function countableUnits(s: ZonedSelection): number {
  return s.isRemoval ? 0 : Math.max(0, s.quantity)
}

/** Suma de unidades que cuentan (por LÍNEA) de un conjunto de selecciones. Para el
 *  contador "llevas N" de la UI — mismo conteo que usa validateZonedGroup. */
export function countZonedUnits(selections: readonly ZonedSelection[]): number {
  return selections.reduce((sum, s) => sum + countableUnits(s), 0)
}

/** Mínimo efectivo del grupo. Paridad exacta con orders/route.ts:589: si
 *  required=false el mínimo es 0 sin importar min_selections, porque el POS permite
 *  saltarse el grupo y servidor y UI tienen que coincidir. */
export function effectiveZonedMin(rule: ZonedGroupRule): number {
  return rule.required ? Math.max(1, rule.minSelections) : 0
}

/**
 * Valida las selecciones de UN grupo dentro de una línea.
 *
 * ORDEN (§7 del spec): agrega primero, valida sobre zonas REALES, y el colapso viene
 * después (fuera de esta función). Validar tras colapsar reportaría "4 whole" en vez
 * de señalar la zona culpable.
 */
export function validateZonedGroup(
  rule: ZonedGroupRule,
  selections: ZonedSelection[],
  layout: ZoneLayout
): ZonedValidation {
  const sels = aggregateZonedSelections(selections)

  for (const s of sels) {
    if (!zoneBelongsToLayout(s.zone, layout)) {
      return { ok: false, reason: "zone_not_in_layout", zone: s.zone }
    }
    if (!rule.zonable && s.zone !== "whole") {
      return { ok: false, reason: "group_not_zonable", zone: s.zone }
    }
  }

  const lineUnits = sels.reduce((sum, s) => sum + countableUnits(s), 0)

  // Contrato de la plataforma (auditoría 2026-07-31, ver checkModifierSelections):
  // max_selections 0 o negativo = SIN tope, igual que null. El form de admin permite
  // teclear 0 y el import por IA no acota; tomarlo literal vuelve el grupo invendible.
  // El grupo de TAMAÑO queda fuera del contrato: su tope es por línea y nunca se
  // vuelve infinito (media familiar y media mediana no existe).
  const maxUnits = rule.maxSelections > 0 ? rule.maxSelections : Infinity

  if (rule.isSizeGroup) {
    // Tamaño: un solo valor por línea, nunca por zona.
    if (lineUnits > Math.max(1, rule.maxSelections)) {
      return { ok: false, reason: "max_per_line" }
    }
  } else {
    // CARGA EFECTIVA de una zona = unidades en 'whole' + unidades en esa zona. Un
    // ingrediente en la pizza entera ocupa físicamente la mitad izquierda también;
    // tratar 'whole' como zona aparte multiplicaría el tope por 5 en 'quarters'.
    const perZone = new Map<Zone, number>()
    for (const s of sels) {
      perZone.set(s.zone, (perZone.get(s.zone) ?? 0) + countableUnits(s))
    }
    const wholeUnits = perZone.get("whole") ?? 0
    if (wholeUnits > maxUnits) {
      return { ok: false, reason: "max_per_zone", zone: "whole" }
    }
    for (const zone of ZONES_BY_LAYOUT[layout]) {
      if (zone === "whole") continue
      const load = wholeUnits + (perZone.get(zone) ?? 0)
      if (load > maxUnits) return { ok: false, reason: "max_per_zone", zone }
    }
  }

  if (lineUnits < effectiveZonedMin(rule)) return { ok: false, reason: "min_per_line" }

  return { ok: true }
}

/** Zonas seleccionables de una disposición: 'whole' (siempre válida) más las propias. */
export function selectableZones(layout: ZoneLayout): Zone[] {
  return layout === "whole" ? ["whole"] : ["whole", ...ZONES_BY_LAYOUT[layout]]
}

export type ZonedBuildState = {
  layout: ZoneLayout
  activeZone: Zone
  /** Los incluidos del ítem (modifier_options.is_default). */
  defaultOptionIds: readonly string[]
  selections: ZonedSelection[]
}

export type ZonedBuildAction =
  | { type: "setLayout"; layout: ZoneLayout }
  | { type: "setActiveZone"; zone: Zone }
  | { type: "addOption"; optionId: string }
  | { type: "removeOption"; optionId: string; zone: Zone }
  | { type: "setServeOnSide"; optionId: string; zone: Zone; onSide: boolean }

export function initZonedBuild(args: {
  layout: ZoneLayout
  defaultOptionIds: readonly string[]
}): ZonedBuildState {
  return {
    layout: args.layout,
    activeZone: "whole",
    defaultOptionIds: args.defaultOptionIds,
    // D7: los incluidos entran en 'whole', marcados, y valen $0 en cualquier zona.
    selections: args.defaultOptionIds.map((optionId) => ({
      optionId,
      zone: "whole" as Zone,
      quantity: 1,
      isRemoval: false,
      serveOnSide: false,
      isIncluded: true,
    })),
  }
}

/** Slot del reductor: la misma opción en la misma zona pero incluida vs extra son
 *  DOS slots (D12). Sin ese eje, doblar el queso incluido saldría gratis. */
function isSlot(s: ZonedSelection, optionId: string, zone: Zone, isIncluded: boolean): boolean {
  return s.optionId === optionId && s.zone === zone && s.isIncluded === isIncluded
}

export function zonedBuildReducer(
  state: ZonedBuildState,
  action: ZonedBuildAction
): ZonedBuildState {
  switch (action.type) {
    case "setLayout": {
      if (action.layout === state.layout) return state
      // D11: 'left' y 'right' no tienen destino definido en 'quarters'. Repartirlos
      // adivina y mantenerlos viola el CHECK de Postgres. Se conserva 'whole'.
      return {
        ...state,
        layout: action.layout,
        activeZone: "whole",
        selections: state.selections.filter((s) => s.zone === "whole"),
      }
    }

    case "setActiveZone": {
      if (!zoneBelongsToLayout(action.zone, state.layout)) return state
      return { ...state, activeZone: action.zone }
    }

    case "addOption": {
      const zone = state.activeZone
      // Volver a agregar algo que se había quitado cancela el removal.
      const removal = state.selections.find(
        (s) => s.optionId === action.optionId && s.zone === zone && s.isRemoval
      )
      if (removal) {
        return {
          ...state,
          selections: state.selections.map((s) =>
            s === removal ? { ...s, isRemoval: false, quantity: 1 } : s
          ),
        }
      }
      // D13 — reponer un incluido que se había quitado de ESTA zona lo devuelve como
      // INCLUIDO ($0), no como extra pago. Sin esto, quitar la aceituna de la
      // izquierda y arrepentirse se la cobraría al cliente.
      //
      // La distinción con D12 es la PRESENCIA en la zona activa, no el tipo de
      // ingrediente: si el incluido YA está acá, sumar crea una unidad extra paga
      // (doble queso); si NO está porque se quitó, sumar lo repone gratis.
      if (state.defaultOptionIds.includes(action.optionId)) {
        const presenteAcá = state.selections.some(
          (s) =>
            s.optionId === action.optionId &&
            s.isIncluded &&
            !s.isRemoval &&
            (s.zone === zone || s.zone === "whole")
        )
        if (!presenteAcá) {
          // Se descarta cualquier remoción global vieja de esta opción: volver a
          // ponerla en una parte contradice el "sin X en toda la pizza".
          const rest = state.selections.filter(
            (s) => !(s.optionId === action.optionId && s.isIncluded && s.isRemoval)
          )
          return {
            ...state,
            selections: [
              ...rest,
              {
                optionId: action.optionId,
                zone,
                quantity: 1,
                isRemoval: false,
                serveOnSide: false,
                isIncluded: true,
              },
            ],
          }
        }
      }

      // D12: la unidad extra vive en su propio slot (isIncluded: false), incluso si
      // el ítem ya trae esa opción incluida.
      const extra = state.selections.find(
        (s) => isSlot(s, action.optionId, zone, false) && !s.isRemoval
      )
      if (extra) {
        return {
          ...state,
          selections: state.selections.map((s) =>
            s === extra ? { ...s, quantity: s.quantity + 1 } : s
          ),
        }
      }
      return {
        ...state,
        selections: [
          ...state.selections,
          {
            optionId: action.optionId,
            zone,
            quantity: 1,
            isRemoval: false,
            serveOnSide: false,
            isIncluded: false,
          },
        ],
      }
    }

    case "removeOption": {
      // Baja primero las unidades EXTRA; la incluida es lo último que se toca.
      const extra = state.selections.find(
        (s) => isSlot(s, action.optionId, action.zone, false) && !s.isRemoval
      )
      if (extra) {
        if (extra.quantity > 1) {
          return {
            ...state,
            selections: state.selections.map((s) =>
              s === extra ? { ...s, quantity: s.quantity - 1 } : s
            ),
          }
        }
        return { ...state, selections: state.selections.filter((s) => s !== extra) }
      }
      // ── D13 (decidido 2026-08-02) ────────────────────────────────────────────
      // Quitar un incluido de ALGUNAS zonas lo MATERIALIZA en las que sobreviven, sin
      // grabar remoción. Quitarlo de TODAS graba una sola remoción en 'whole'.
      //
      // Por qué no una remoción zoneada: la capa de lectura renderiza las filas tal
      // como se guardan, sin restar. "incluido en whole" + "removido en left" imprime
      // `ENTERA: aceituna / IZQUIERDA: - aceituna`, que es el cross-reference que §9
      // existe para evitar — y un pizzero que escanea ENTERA le pone aceitunas a toda
      // la pizza antes de leer la línea que lo contradice.
      const layoutZones = ZONES_BY_LAYOUT[state.layout] as readonly Zone[]
      const mkIncluded = (zone: Zone, isRemoval: boolean): ZonedSelection => ({
        optionId: action.optionId,
        zone,
        quantity: 1,
        isRemoval,
        serveOnSide: false,
        isIncluded: true,
      })

      const wholeIncluded = state.selections.find(
        (s) => isSlot(s, action.optionId, "whole", true) && !s.isRemoval
      )

      // Quitar desde 'whole' = quitarlo de toda la pizza, incluso si estaba
      // materializado por partes.
      if (action.zone === "whole") {
        const rest = state.selections.filter(
          (s) => !(s.optionId === action.optionId && s.isIncluded)
        )
        if (rest.length === state.selections.length) return state
        return { ...state, selections: [...rest, mkIncluded("whole", true)] }
      }

      if (wholeIncluded) {
        const survivors = layoutZones.filter((z) => z !== action.zone)
        const rest = state.selections.filter((s) => s !== wholeIncluded)
        // Sin sobrevivientes (disposición de una sola zona) equivale a quitarlo entero.
        if (survivors.length === 0) return { ...state, selections: [...rest, mkIncluded("whole", true)] }
        return { ...state, selections: [...rest, ...survivors.map((z) => mkIncluded(z, false))] }
      }

      // Ya estaba materializado: se borra esta zona. Si era la última, la ausencia
      // deja de ser silenciosa y pasa a ser una remoción explícita de toda la pizza.
      const zoneIncluded = state.selections.find(
        (s) => isSlot(s, action.optionId, action.zone, true) && !s.isRemoval
      )
      if (zoneIncluded) {
        const rest = state.selections.filter((s) => s !== zoneIncluded)
        const sobrevive = rest.some(
          (s) => s.optionId === action.optionId && s.isIncluded && !s.isRemoval
        )
        return {
          ...state,
          selections: sobrevive ? rest : [...rest, mkIncluded("whole", true)],
        }
      }
      return state
    }

    case "setServeOnSide": {
      // D6: "aparte" solo existe en la pizza entera. Un ingrediente no puede estar en
      // la mitad izquierda y en un platito al mismo tiempo.
      if (action.zone !== "whole") return state
      return {
        ...state,
        selections: state.selections.map((s) =>
          s.optionId === action.optionId && s.zone === action.zone
            ? { ...s, serveOnSide: action.onSide }
            : s
        ),
      }
    }
  }
}

/**
 * Selecciones tal como el servidor las va a cotizar: agregadas y colapsadas.
 *
 * La UI DEBE mostrar el precio calculado sobre esto. Sumar las selecciones crudas
 * daría $1.50 + $1.50 = $3.00 mientras el servidor cobra $2.99.
 */
export function normalizedSelections(state: ZonedBuildState): ZonedSelection[] {
  return collapseFullCoverage(state.selections, state.layout)
}

/** Una selección normalizada con su precio ya resuelto, en CENTAVOS por unidad. */
export type PricedZonedSelection = ZonedSelection & { priceDeltaCents: number }

/**
 * Selecciones normalizadas CON PRECIO. Es la única resolución de precio del armador:
 * el total en vivo y la línea que se manda al carrito salen de este mismo arreglo, así
 * que no pueden discrepar.
 *
 * Antes solo existía el total agregado (`zonedBuildSubtotalCents`) y el precio por fila
 * se perdía en el camino: los tres modales escribían `price_delta: 0` en cada topping y
 * `calculateOrderTotals` sumaba esos ceros — el carrito enseñaba la pizza sin extras
 * mientras la caja cobraba la de verdad (ver lib/menu/__tests__/zonedCartParity.test.ts).
 */
export function priceZonedBuild(
  state: ZonedBuildState,
  resolve: (s: ZonedSelection) => number
): PricedZonedSelection[] {
  return normalizedSelections(state).map((s) => ({
    ...s,
    // §6.5 — la remoción vale 0 pase lo que pase, el MISMO candado que aplica
    // `quoteZonedLine` en el servidor. Hoy toda remoción es de un incluido (D13) y
    // `resolve` ya devolvería 0; el candado explícito es para que siga siendo cierto.
    priceDeltaCents: s.isRemoval ? 0 : resolve(s),
  }))
}

/** Suma de un armado ya cotizado, en CENTAVOS. */
export function sumZonedCents(priced: readonly PricedZonedSelection[]): number {
  return priced.reduce((sum, s) => sum + s.priceDeltaCents * s.quantity, 0)
}

/**
 * Subtotal de modificadores EN CENTAVOS para mostrar en vivo. `resolve` devuelve los
 * centavos por unidad de una selección — normalmente un cierre sobre
 * resolveZonedUnitPriceCents con la rejilla ya cargada. El módulo no lee la base.
 */
export function zonedBuildSubtotalCents(
  state: ZonedBuildState,
  resolve: (s: ZonedSelection) => number
): number {
  return sumZonedCents(priceZonedBuild(state, resolve))
}
